import React, { useState } from 'react';

const TIMELINE_COLOR = '#FFCC00';

function getInitials(name: string | null | undefined): string {
  if (!name) {
    return '';
  }
  return name
    .trim()
    .split(/\s+/)
    .map((n) => n[0] ?? '')
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

// ---------------------------------------------------------------------------
// TimelineAvatarCircle
// 48px outer dashed ring (conic gradient) + 40px avatar, 4px gap
// bgColor must match the card/page background so the conic cutout blends in
// ---------------------------------------------------------------------------

interface TimelineAvatarCircleProps {
  name: string;
  imageUrl?: string | undefined;
}

export const TimelineAvatarCircle: React.FC<TimelineAvatarCircleProps> = ({
  name,
  imageUrl,
}) => {
  const [imgError, setImgError] = useState(false);
  const showImage = !!imageUrl && !imgError;

  return (
    <div
      data-testid="timeline-avatar-circle"
      style={{ position: 'relative', width: 48, height: 48, flexShrink: 0 }}
    >
      {/* SVG dashed ring — clean evenly-spaced dashes at any size */}
      <svg
        width="48"
        height="48"
        style={{ position: 'absolute', top: 0, left: 0 }}
      >
        <circle
          cx="24"
          cy="24"
          r="23"
          fill="none"
          stroke={TIMELINE_COLOR}
          strokeWidth="1"
          strokeDasharray="4 6"
          strokeLinecap="round"
        />
      </svg>
      {/* Avatar — centred inside the ring */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 40,
          height: 40,
          borderRadius: '50%',
          backgroundColor: '#E8EEF0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        {showImage ? (
          <img
            src={imageUrl}
            alt={name}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={() => setImgError(true)}
          />
        ) : (
          <span style={{ color: '#223746', fontSize: 14, fontWeight: 500 }}>
            {getInitials(name)}
          </span>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// TimelineConnector
// Dashed vertical line (repeating-linear-gradient) + CSS ^ chevron in middle
// bgColor must match card background so the chevron masks the line behind it
// ---------------------------------------------------------------------------

interface TimelineConnectorProps {
  bgColor?: string;
  testId?: string;
}

export const TimelineConnector: React.FC<TimelineConnectorProps> = ({
  bgColor = '#ffffff',
  testId,
}) => {
  const lineStyle: React.CSSProperties = {
    flex: 1,
    width: 1,
    minHeight: 12,
    background: `repeating-linear-gradient(to bottom, ${TIMELINE_COLOR} 0px, ${TIMELINE_COLOR} 6px, transparent 6px, transparent 12px)`,
  };

  return (
    <div
      data-testid={testId}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        flex: 1,
        minHeight: 56,
        marginTop: 2,
      }}
    >
      <div style={lineStyle} />

      {/* ^ chevron: border-top + border-left rotated 45°
          Wrapper background masks the dashed line behind the chevron.
          Vertical padding must cover the rotated overflow: 14px div rotated
          45° has visual height ≈ 14×√2 ≈ 20px → need ~4px top/bottom padding */}
      <div
        style={{
          background: bgColor,
          padding: '4px 8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <div
          style={{
            width: 14,
            height: 14,
            borderTop: `1px solid ${TIMELINE_COLOR}`,
            borderLeft: `1px solid ${TIMELINE_COLOR}`,
            transform: 'rotate(45deg)',
          }}
        />
      </div>

      <div style={lineStyle} />
    </div>
  );
};
